import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { CertificationsInterface } from 'constants/interfaces';
import { certificationInitialValues, certificationSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import useUserStore from 'store/user';
import { useUpdateUserDetailsMutation } from 'services';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { formatDateWithYear, getMinDate } from 'utils';
import useCommonStore from 'store/common';

const AddCertifications: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { certification: savedData = [] } = profileData;
	const { modalConfig = {} }: any = useCommonStore((state) => state);

	const formik: any = useFormik({
		initialValues: {
			...certificationInitialValues,
			...(Number.isInteger(modalConfig?.id) && savedData[modalConfig.id]
				? {
						...savedData[modalConfig.id],
						completionYear: formatDateWithYear(savedData[modalConfig.id].completionYear),
					}
				: {}),
		},
		validationSchema: certificationSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			const newDatas = savedData ? [...savedData] : [];
			if (Number.isInteger(modalConfig?.id)) {
				newDatas[modalConfig.id] = {
					...values,
					completionYear: formik.values?.completionYear?.getFullYear(),
				};
			} else {
				newDatas.push({
					...values,
					completionYear: formik.values?.completionYear?.getFullYear(),
				});
			}

			updateProfile({ certification: newDatas, saveType: 'addCertificate' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});

	const renderError = <T extends keyof CertificationsInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.addCertification')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Profile.certificationInstituteName')}
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterInstituteName')}
									name="instituteName"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.instituteName}
									autoComplete="off"
								/>
								{renderError('instituteName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.courseName')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterCourseName')}
									name="courseName"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.courseName}
									autoComplete="off"
								/>
								{renderError('courseName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.completionYear')}</Form.Label>
								<DatePicker
									selected={formik.values.completionYear}
									onChange={(date: any) => formik.setFieldValue('completionYear', date)}
									name="completionYear"
									onBlur={formik.handleBlur}
									dateFormat="yyyy"
									showYearPicker
									minDate={getMinDate()}
									placeholderText={`${translate('PlaceHolder.onlyYear')}`}
									maxDate={new Date()}
									autoComplete="off"
								/>
								{renderError('completionYear')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddCertifications;
